import type { CSSProperties } from "react";
import { AttendeeNote, User } from "../../types/Types";

export type NoteTypeFilter = "" | "personal" | "event";

export const CARD_SHADOW: CSSProperties = {
  boxShadow: "0 0.125rem 0.5rem rgba(0, 0, 0, 0.08)",
};

/** Keeps primary (custom padding) and outline buttons the same height. */
export const NOTE_ACTION_BTN_STYLE: CSSProperties = {
  height: 38,
  minHeight: 38,
  paddingTop: 0,
  paddingBottom: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

export const NOTE_CLAMP: CSSProperties = {
  whiteSpace: "pre-wrap",
  display: "-webkit-box",
  WebkitLineClamp: 4,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  minHeight: 72,
};

export function formatNoteDate(
  value: string | null | undefined,
  locale: string
) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function attendeeAsUser(note: AttendeeNote): User {
  return {
    id: note.attendeeUserId,
    objectId: note.attendeeObjectId,
    firstName: note.attendeeFirstName,
    lastName: note.attendeeLastName,
  } as User;
}

export function buildSelfUser(
  currentUser: User | undefined,
  objectId: string | null
): User | null {
  if (!currentUser && !objectId) return null;
  return {
    ...(currentUser ?? {}),
    id: currentUser?.id ?? 0,
    objectId: currentUser?.objectId || objectId || "",
    firstName: currentUser?.firstName || "",
    lastName: currentUser?.lastName || "",
  } as User;
}

export function getEventOptionsFromNotes(notes: AttendeeNote[]): string[] {
  const names = new Set<string>();
  for (const n of notes) {
    const name = (n.eventName || "").trim();
    if (n.eventId > 0 && name) names.add(name);
  }
  return Array.from(names).sort((a, b) => a.localeCompare(b));
}

export function filterAttendeeNotes(
  notes: AttendeeNote[],
  opts: {
    searchTerm: string;
    typeFilter: NoteTypeFilter;
    eventFilter: string;
    personalLabel: string;
    youLabel: string;
  }
): AttendeeNote[] {
  const term = opts.searchTerm.trim().toLowerCase();
  return notes.filter((n) => {
    const isPersonal = !n.eventId;
    if (opts.typeFilter === "personal" && !isPersonal) return false;
    if (opts.typeFilter === "event" && isPersonal) return false;
    if (opts.eventFilter && (n.eventName || "").trim() !== opts.eventFilter) {
      return false;
    }
    if (!term) return true;
    const name = `${n.attendeeFirstName} ${n.attendeeLastName}`.toLowerCase();
    const event = (n.eventName || "").toLowerCase();
    const text = (n.noteText || "").toLowerCase();
    const personal = opts.personalLabel.toLowerCase();
    const you = opts.youLabel.toLowerCase();
    return (
      name.includes(term) ||
      event.includes(term) ||
      text.includes(term) ||
      (isPersonal && (personal.includes(term) || you.includes(term)))
    );
  });
}
